import React, { useState, useLayoutEffect, useContext, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '@/layout/Auth'
import Text from '@/components/Text'
import { Row } from '../components'
import { ImageIcon } from '@/icon'
import Button from '@/components/Button'
import warning_icon from '@/asset/img/warning_icon.webp'
import warning_icon_white from '@/asset/img/warning_icon_white.webp'
import { grayText2 } from '@/constant'
import { useMutation } from '@tanstack/react-query'
import { cancelOrder } from '@/api'
import { confirm } from '@/components/Modal'
import { PayTypeEnum } from '@/types'
import AssistantCode from '@/layout/AssistantCode'
import { getDurationString } from '../../../Space/Order/utils'
import { Message } from '@cx/ui'

const Payment = ({
  orderCode,
  endTime,
  hover,
  paid,
  showTime,
  status,
  payType,
}: {
  orderCode: string
  endTime: number
  hover?: boolean
  paid: boolean
  showTime: boolean
  status?: number // 1:未支付；2:已支付；3:已退款；4:已取消 5:超时关闭
  payType?: number
}) => {
  const { authInfo } = useContext(AuthContext)
  const orgId = authInfo?.orgInfo?.id
  const navigate = useNavigate()
  const [seconds, setSecond] = useState<number>(endTime)
  const cancelOrderMutation = useMutation(cancelOrder, {
    onSuccess: () => {
      Message.success('订单已取消')
      navigate(0)
    },
  })

  useEffect(() => {
    setSecond(endTime)
  }, [endTime])

  useLayoutEffect(() => {
    const countDown = setInterval(() => {
      if (endTime > 0) {
        setSecond((second) => {
          if (second <= 1) {
            navigate(0)
            return 0
          } else {
            return second - 1
          }
        })
      }
    }, 1000)

    return () => {
      clearInterval(countDown)
    }
  }, [endTime, navigate])

  const payPage = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation()
      navigate(`/console/organization/orders/detail?order_code=${orderCode}`)
    },
    [navigate, orderCode],
  )

  const onCancel = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation()
      if (orderCode && orgId) {
        confirm({
          title: '提示',
          content: '订单取消成功后将无法恢复，已选择优惠券会原路返还。',
          closeAfterClose: true,
          // @ts-ignore
          onClose: null,
          onConfirm: () => cancelOrderMutation.mutate({ order_code: orderCode }),
        })
      }
    },
    [cancelOrderMutation, orderCode, orgId],
  )

  return (
    <>
      <Row sx={{ marginTop: '24px', alignItems: 'center' }}>
        {showTime && (
          <>
            <ImageIcon
              sx={{
                width: '14px',
                marginRight: '8px',
                marginTop: '-1px',
              }}
              src={hover ? warning_icon_white : warning_icon}
            />
            <Text size='xs' sx={{ color: hover ? '#fff' : grayText2 }}>
              {seconds > 0
                ? `剩余支付时间${getDurationString(seconds)}，否则订单将自动取消。`
                : '订单已过期，请重新下单'}
            </Text>
          </>
        )}
      </Row>

      <Row sx={{ justifyContent: 'space-between', marginTop: 'auto' }}>
        {seconds > 0 && status === 1 ? (
          <>
            <Button
              type='cancel'
              sx={{
                ...(hover && {
                  color: '#fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.30)',
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'rgba(255, 255, 255, 0.30)',
                  },
                }),
              }}
              onClick={onCancel}
            >
              取消订单
            </Button>
            <Button
              sx={{ fontSize: '16px', width: '120px' }}
              onClick={payPage}
              type={hover ? 'secondary' : 'highlight'}
            >
              去支付
            </Button>
          </>
        ) : (
          paid &&
          payType !== PayTypeEnum.voucher && (
            <AssistantCode>
              <div>
                <Button
                  type='cancel'
                  sx={{
                    ...(hover && {
                      color: '#fff',
                      backgroundColor: 'rgba(255, 255, 255, 0.30)',
                      '&:hover': {
                        color: '#fff',
                        backgroundColor: 'rgba(255, 255, 255, 0.30)',
                      },
                    }),
                  }}
                >
                  申请发票
                </Button>
              </div>
            </AssistantCode>
          )
        )}
      </Row>
    </>
  )
}

export default Payment
